import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Video, Calendar, Clock, CheckCircle, Loader, CreditCard, Sparkles, DollarSign } from 'lucide-react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { api, API } from '../lib/auth';
import { formatDateTime, cn } from '../lib/utils';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const SESSION_TYPES = [
  { value: '15min', label: '15 Minutes', price: 30, description: 'Quick strategy call' },
  { value: '30min', label: '30 Minutes', price: 60, description: 'Standard consultation' },
  { value: '60min', label: '1 Hour', price: 120, description: 'Deep dive session' },
];

// Generate time slots from 8am to 8pm
const generateTimeSlots = () => {
  const slots = [];
  for (let hour = 8; hour <= 20; hour++) {
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    slots.push({ value: `${hour}:00`, label: `${displayHour}:00 ${period}` });
    if (hour < 20) {
      slots.push({ value: `${hour}:30`, label: `${displayHour}:30 ${period}` });
    }
  }
  return slots;
};

const TIME_SLOTS = generateTimeSlots();

// Generate available dates (next 30 days, excluding Sundays, starting 24h from now)
const generateAvailableDates = () => {
  const dates = [];
  const now = new Date();
  const minDate = new Date(now.getTime() + 24 * 60 * 60 * 1000); // 24 hours from now
  
  for (let i = 0; i < 45; i++) {
    const date = new Date(minDate);
    date.setDate(minDate.getDate() + i);
    
    // Skip Sundays (0 = Sunday)
    if (date.getDay() !== 0) {
      dates.push({
        value: date.toISOString().split('T')[0],
        label: date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
        dayOfWeek: date.toLocaleDateString('en-US', { weekday: 'long' })
      });
    }
    
    if (dates.length >= 30) break;
  }
  return dates;
};

export default function ExpertSessionPage() {
  const [departments, setDepartments] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    department_id: '',
    topic: '',
    session_type: '30min',
    selected_date: '',
    selected_time: '',
    notes: ''
  });
  
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const availableDates = useMemo(() => generateAvailableDates(), []);
  const selectedSession = SESSION_TYPES.find(s => s.value === formData.session_type);
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  useEffect(() => {
    loadData();
    
    // Handle success redirect from Stripe
    const success = searchParams.get('success');
    const sessionId = searchParams.get('session_id');
    
    if (success === 'true' && sessionId) {
      // Verify payment and show success
      verifyPaymentAndRedirect(sessionId);
    }
  }, [searchParams]);
  
  const verifyPaymentAndRedirect = async (sessionId) => {
    try {
      // Verify payment status
      const token = localStorage.getItem('token');
      await axios.post(`${API}/expert-sessions/${sessionId}/verify-payment`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Show success message
      toast.success('🎉 Session Booked Successfully! Your DWY session has been paid and confirmed. Redirecting to dashboard...', {
        duration: 5000
      });
      
      // Redirect to dashboard after 3 seconds
      setTimeout(() => {
        navigate('/dashboard');
      }, 3000);
    } catch (error) {
      console.error('Payment verification error:', error);
      toast.success('Session booked! Please check your dashboard for details.');
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    }
  };

  const loadData = async () => {
    try {
      const [deptsRes, bookingsRes] = await Promise.all([
        api.getDepartments(),
        api.getZoomRequests()
      ]);
      setDepartments(deptsRes.data);
      setBookings(bookingsRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.department_id || !formData.topic || !formData.selected_date || !formData.selected_time) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      // Create session booking with payment
      const response = await api.createExpertSession({
        department_id: formData.department_id,
        topic: formData.topic,
        session_type: formData.session_type,
        selected_date: formData.selected_date,
        selected_time: formData.selected_time,
        timezone: userTimezone,
        notes: formData.notes,
        price: selectedSession.price
      });
      
      // Redirect to Stripe checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.success('Session booked successfully!');
        loadData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to book session');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="font-heading text-3xl font-bold text-white mb-2">
            Book an Expert DWY Session
          </h1>
          <p className="text-gray-400">Get hands-on guidance from our department experts via Zoom</p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Booking Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass rounded-2xl p-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                <Video className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h2 className="font-semibold text-white">Book Your Session</h2>
                <p className="text-sm text-gray-400">Select a date, time, and session length</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Session Type with Pricing */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">Session Length & Price *</label>
                <div className="grid grid-cols-3 gap-3">
                  {SESSION_TYPES.map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, session_type: type.value })}
                      className={cn(
                        "p-4 rounded-xl border-2 transition-all text-center",
                        formData.session_type === type.value
                          ? "border-primary bg-primary/10"
                          : "border-white/10 hover:border-white/30"
                      )}
                      data-testid={`session-type-${type.value}`}
                    >
                      <div className="font-bold text-white text-lg">{type.label}</div>
                      <div className="text-2xl font-bold text-primary mt-1">${type.price}</div>
                      <div className="text-xs text-gray-400 mt-1">{type.description}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Department */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">Department *</label>
                <Select 
                  value={formData.department_id} 
                  onValueChange={(value) => setFormData({ ...formData, department_id: value })}
                >
                  <SelectTrigger className="input-dark" data-testid="session-department-select">
                    <SelectValue placeholder="Select a department" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-white/10 max-h-60">
                    {departments.map((dept) => (
                      <SelectItem key={dept.id} value={dept.id}>
                        {dept.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Topic */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">What do you need help with? *</label>
                <Textarea
                  value={formData.topic}
                  onChange={(e) => setFormData({ ...formData, topic: e.target.value })}
                  placeholder="e.g., I need help setting up my first automation workflow and understanding the best strategy for lead capture..."
                  className="input-dark min-h-[100px]"
                  data-testid="session-topic-input"
                />
              </div>

              {/* Date Selection */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">Select Date *</label>
                <Select 
                  value={formData.selected_date} 
                  onValueChange={(value) => setFormData({ ...formData, selected_date: value })}
                >
                  <SelectTrigger className="input-dark" data-testid="session-date-select">
                    <SelectValue placeholder="Choose a date" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-white/10 max-h-60">
                    {availableDates.map((date) => (
                      <SelectItem key={date.value} value={date.value}>
                        {date.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">Available Mon-Sat, starting 24 hours from now</p>
              </div>

              {/* Time Selection */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">Select Time * <span className="text-xs text-gray-500">({userTimezone})</span></label>
                <Select 
                  value={formData.selected_time} 
                  onValueChange={(value) => setFormData({ ...formData, selected_time: value })}
                >
                  <SelectTrigger className="input-dark" data-testid="session-time-select">
                    <SelectValue placeholder="Choose a time slot" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-white/10 max-h-60">
                    {TIME_SLOTS.map((slot) => (
                      <SelectItem key={slot.value} value={slot.value}>
                        {slot.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">Available 8:00 AM - 8:00 PM</p>
              </div>

              {/* Additional Notes */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">Additional Notes (Optional)</label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Any specific questions or materials you'd like to discuss..."
                  className="input-dark"
                  rows={3}
                  data-testid="session-notes-input"
                />
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={submitting || !formData.department_id || !formData.topic || !formData.selected_date || !formData.selected_time}
                className="btn-primary w-full py-6 text-lg"
                data-testid="book-session-btn"
              >
                {submitting ? (
                  <>
                    <Loader className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5 mr-2" />
                    Book & Pay ${selectedSession?.price || 50}
                  </>
                )}
              </Button>
            </form>
          </motion.div>

          {/* Info Panel */}
          <div className="space-y-6">
            {/* Session Fee Discount Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="glass rounded-2xl p-6 border-2 border-accent/30"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-accent" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">It's Essentially FREE!</h3>
                  <p className="text-sm text-accent">Session fee discounted from orders</p>
                </div>
              </div>
              <p className="text-gray-300 mb-4">
                Your department expert will help you with <strong className="text-white">strategy</strong>, share their <strong className="text-white">experience</strong>, and help you <strong className="text-white">create your order</strong> with confidence.
              </p>
              <div className="bg-white/5 rounded-lg p-4">
                <div className="flex items-center gap-2 text-accent mb-2">
                  <DollarSign className="w-4 h-4" />
                  <span className="font-semibold">Session Fee Discount</span>
                </div>
                <p className="text-sm text-gray-400">
                  The session fee you pay today will be <strong className="text-white">discounted from your total order balance</strong> when you place an order after your session. So in essence, it's a free consultation!
                </p>
              </div>
            </motion.div>

            {/* What to Expect */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="glass rounded-2xl p-6"
            >
              <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                <Video className="w-5 h-5 text-primary" />
                What to Expect
              </h3>
              <ul className="space-y-3">
                {[
                  'Live Zoom call with a department expert',
                  'Personalized strategy for your specific needs',
                  'Help creating your project brief/order',
                  'Q&A and hands-on guidance',
                  'Session recording available upon request'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-gray-300">
                    <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Your Upcoming Sessions */}
            {bookings.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="glass rounded-2xl p-6"
              >
                <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Your Sessions
                </h3>
                <div className="space-y-3">
                  {bookings.slice(0, 5).map((booking) => (
                    <div 
                      key={booking.id}
                      className="p-3 rounded-lg bg-white/5 border border-white/10"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-white text-sm">{booking.topic}</span>
                        <span className={cn(
                          "px-2 py-0.5 rounded-full text-xs font-medium",
                          booking.status === 'confirmed' ? "bg-green-500/20 text-green-400" :
                          booking.status === 'pending' ? "bg-yellow-500/20 text-yellow-400" :
                          "bg-gray-500/20 text-gray-400"
                        )}>
                          {booking.status}
                        </span>
                      </div>
                      <p className="text-xs text-gray-400">{booking.department_name}</p>
                      {booking.zoom_link && (
                        <a 
                          href={booking.zoom_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-primary hover:underline mt-2 inline-block"
                        >
                          Join Zoom Call →
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
